import { useEffect } from 'react';

import { EyeIcon, EyeOffIcon, Square, SquareDashed } from 'lucide-react';
import { Button, DefaultTooltip, DefaultPopover, ToggleOptionGroup, ToggleOptionGroupItem } from 'twyne-new.ui';

import { useResponsive } from '@/modules/cms/views/post/components/builder/context';

import { useCraftProp } from '../utils';

import SliderProperty from './Slider';

export default function VisibilityProperty() {
  return <>
    <div className='flex items-center justify-between gap-2 relative'>
      <Opacity />
      <HideToggle />
    </div>
  </>
}

const Opacity = () => {
  const { value: hidden } = useCraftProp('hidden')

  return <SliderProperty
    name="opacity"
    min={0}
    max={100}
    step={1}
    defaultValue={100}
    disabled={hidden === 'hidden'}
  />
}

const HideToggle = () => {
  const { rawValue, value, setValue: setHide } = useCraftProp('hide')
  const { device } = useResponsive()

  useEffect(() => {
    if (['hide', 'show'].includes(value)) {
      return setHide(value === 'hide' ? 'hidden' : 'block')
    }
  }, [value, setHide])

  useEffect(() => {
    if (!rawValue) return;

    const currentDeviceValue = value
    const otherDeviceValue = rawValue?.[inverseOf(device)]

    if (currentDeviceValue === otherDeviceValue && ['hidden', 'invisible'].includes(currentDeviceValue)) {
      return;
    }
    if (!currentDeviceValue && ['block', 'visible'].includes(otherDeviceValue)) {
      return setHide(undefined, inverseOf(device))
    }
    if (!otherDeviceValue) {
      return setHide(inverseOf(currentDeviceValue), inverseOf(device))
    }
  }, [value, rawValue, device, setHide])

  const handleChange = currentDeviceValue => {
    const otherDeviceValue = rawValue?.[inverseOf(device)]
    if (!currentDeviceValue && ['hidden', 'invisible'].includes(otherDeviceValue)) {
      return setHide(inverseOf(otherDeviceValue))
    }
    setHide(currentDeviceValue)
  }

  const toggleValue = typeof rawValue === 'string' ? rawValue : rawValue?.[device]
  const triggerConfig = getTriggerConfig({ value })

  return <DefaultPopover
    key={toggleValue}
    trigger={<Button variant='light' size="sm">
      <DefaultTooltip content={<>
        <div>Visibility</div>
        {triggerConfig.tooltip && <div className='text-sm font-bold'>{triggerConfig.tooltip}</div>}
      </>}>
        {triggerConfig.icon}
      </DefaultTooltip>
    </Button>}
    triggerProps={{ asChild: true }}
    contentProps={{ className: 'p-0 w-12', side: 'bottom', align: 'center' }}
  >
    <ToggleOptionGroup size='lg' value={toggleValue} onValueChange={handleChange} type='single' className='flex flex-col gap-1'>
      <ToggleOptionGroupItem value='hidden'>
        <DefaultTooltip content={<>
          <div>Hide the element from the screen</div>
          <div className='text-sm font-bold'>Element will be hidden, and not take up space</div>
        </>}>
          {value === 'hidden' ? <EyeOffIcon size={16} /> : <EyeIcon size={16} />}
        </DefaultTooltip>
      </ToggleOptionGroupItem>
      <ToggleOptionGroupItem value='invisible'>
        <DefaultTooltip content={<>
          <div>Make element invisible on the screen</div>
          <div className='text-sm font-bold'>Element will be invisible, but still take up space</div>
        </>}>
          {value === 'invisible' ? <SquareDashed size={16} /> : <Square size={16} />}
        </DefaultTooltip>
      </ToggleOptionGroupItem>
    </ToggleOptionGroup>
  </DefaultPopover>
}

const getTriggerConfig = ({ value }) => {
  const config = {
    hidden: {
      icon: <EyeOffIcon size={16} />,
      tooltip: 'Element is hidden.',
    },
    block: {
      icon: <EyeIcon size={16} />,
      tooltip: 'Element is shown.',
    },
    invisible: {
      icon: <SquareDashed size={16} />,
      tooltip: 'Element is invisible.',
    },
    visible: {
      icon: <Square size={16} />,
      tooltip: 'Element is visible.',
    },
  }

  return config[value] || { icon: <EyeIcon size={16} />, tooltip: '' }
}

const inverseOf = value => ({
  desktop: 'mobile',
  mobile: 'desktop',
  hidden: 'block',
  block: 'hidden',
  invisible: 'visible',
  visible: 'invisible',
}[value])
